import dayjs from 'dayjs';
import {goalIsOverdueCarryover} from '../my-objective/my-goals-v2.utils';
import {MyGoalsPeriod} from '../my-objective/my-goals-v2.types';

const entityId = (value: any): string =>
  typeof value === 'string' ? value : value?.id || value?._id || '';

const responseCollection = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.users)) return response.users;
  if (Array.isArray(response?.goals)) return response.goals;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data && response.data !== response) {
    return responseCollection(response.data);
  }
  return [];
};

export const goalMemberBucketsFromResponse = (response: any): any[] => {
  const collection = responseCollection(response);
  if (!collection.length) return [];

  if (collection.some((item) => Array.isArray(item?.goals))) {
    return collection.filter((item) => Array.isArray(item?.goals));
  }

  const buckets = new Map<string, any>();
  collection.forEach((goal) => {
    const assignee = goal?.assignee;
    const assigneeId = entityId(assignee) || 'unassigned';
    const existing = buckets.get(assigneeId);
    if (existing) {
      existing.goals.push(goal);
      return;
    }

    buckets.set(assigneeId, {
      ...(assignee && typeof assignee === 'object' ? assignee : {}),
      id: assigneeId === 'unassigned' ? '' : assigneeId,
      goals: [goal],
    });
  });

  return Array.from(buckets.values());
};

export const goalMemberBucketsWithCarryover = (
  activeResponse: any,
  workspaceResponse: any,
  period: MyGoalsPeriod,
  today = dayjs(),
): any[] => {
  const buckets = goalMemberBucketsFromResponse(
    activeResponse,
  ).map((member) => ({...member, goals: [...member.goals]}));
  const byUserId = new Map(buckets.map((member) => [entityId(member), member]));

  goalMemberBucketsFromResponse(workspaceResponse).forEach((member) => {
    const carryoverGoals = member.goals.filter((goal: any) =>
      goalIsOverdueCarryover(goal, period, today),
    );
    if (!carryoverGoals.length) return;

    const userId = entityId(member);
    const existing = byUserId.get(userId);
    if (!existing) {
      const added = {...member, goals: [...carryoverGoals]};
      buckets.push(added);
      byUserId.set(userId, added);
      return;
    }

    const existingGoalIds = new Set(
      existing.goals.map((goal: any) => entityId(goal)),
    );
    carryoverGoals.forEach((goal: any) => {
      if (existingGoalIds.has(entityId(goal))) return;
      existing.goals.push(goal);
      existingGoalIds.add(entityId(goal));
    });
  });

  return buckets;
};

export const goalMemberBucketsForView = (
  response: any,
  view: 'flat' | 'stacked' | 'tree',
): any[] => {
  const goalMembers = goalMemberBucketsFromResponse(response);
  if (view !== 'stacked') return goalMembers;

  return goalMembers
    .map((member) => ({
      ...member,
      goals: Array.isArray(member?.goals)
        ? member.goals.filter(
            (goal: any) =>
              Array.isArray(goal?.alignedGoals) && goal.alignedGoals.length > 0,
          )
        : [],
    }))
    .filter((member) => member.goals.length > 0);
};
